import { Grid } from '@mui/material';
import { AddItemButton } from '@tassat/button';
import { ActionTypes, AdminRoleId, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { UserWalletMappingForm } from '@tassat/forms';
import { UserAccountMapped } from '@tassat/fred-extended-definitions';
import { FormModal } from '@tassat/modal';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { submitUserWalletMappingForm } from 'Containers/admin/forms/helpers/userAccountMappingFormFn';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { AdminState } from 'Reducers/store/adminStore';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUnmappedTokenWalletsByUserId } from 'Selectors/userAccounts';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import { ClientRoleEnum } from 'SharedComponents/users/ClientRoleEnum';
import UserWalletGrid from 'SharedComponents/users/userWalletGrid';

import { FirmUserWalletsProps } from './types';

export function FirmUserWallets(props: FirmUserWalletsProps) {
    const {
        classes,
        firm,
        user,
        userRole
    } = props;

    const accounts = useAppSelector((state) => getUnmappedTokenWalletsByUserId(state as AdminState, user.FirmId, user.UserId));
    const [features] = useFeatures();
    const [ mapUserAccountModalOpen, setMapUserAccountModalOpen ] = React.useState<boolean>(false);
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const unmapUserFromAccountValidation = async (userAccountMapped: UserAccountMapped) => {
        const user = userAccountMapped.user;
        const account = userAccountMapped.account;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: userAccountMapped,
                actionType: ActionTypes.unmap,
                confirmTitle: `Confirm Unmapping ${user.Email}`,
                confirmBody: `Please confirm removing permissions to account ${account.AccountName} for user ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const unmapUserFromAccount = async (userAccountMapped: UserAccountMapped) => {
        const userAccount = userAccountMapped.userAccount;
        const user = userAccountMapped.user;
        const account = userAccountMapped.account;

        try {
            const response = await Worker.getInstance()
                .updateUserAccount({
                    UserId: user.UserId,
                    AccountId: account.AccountId,
                    ClientRoleId: ClientRoleEnum.NOT_ASSIGNED,
                    WireRoleId: userAccount.WireRoleId,
                    UserAccountId: userAccount.UserAccountId,
                    IsFavorite: userAccount.IsFavorite,
                    IsDeleted: userAccount.IsDeleted
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Unmapped User ${user.Email}`,
                        successBody: `Successfully unmapped the user ${user.Email} from account ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Unmap User ${user.Email}`,
                        failureBody: `Could not unmap the user ${user.Email} from account ${account.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else {
                throw new Error();
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Unmap User ${user.Email}`,
                    failureBody: `Something went wrong unmapping the user ${user.Email} from account ${account.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                setSelectedItem: () => user,
                preActionValidationAndConfig: { [ActionTypes.unmap]: unmapUserFromAccountValidation },
                onConfirm: { [ActionTypes.unmap]: unmapUserFromAccount },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                {userRole.AdminRoleId !== AdminRoleId.BANK_OPS_SUPERVISOR &&
                    userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN &&
                    !firm?.IsClosed &&
                    (
                    <Grid
                        className={classes.mapWalletContainer}
                        item
                    >
                        <AddItemButton
                            onClick={() => setMapUserAccountModalOpen(true)}
                            text='Map Account(s)'
                        />
                    </Grid>
                )}
                <UserWalletGrid
                    firm={firm}
                    user={user}
                />
                <FormModal
                    Form={UserWalletMappingForm}
                    formProps={{
                        user: user,
                        accounts: accounts,
                        features,
                        submitUserWalletMappingForm: submitUserWalletMappingForm
                    }}
                    onClose={() => setMapUserAccountModalOpen(false)}
                    open={mapUserAccountModalOpen}
                    title='Map Account(s)'
                />
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}